import { Drawer, Link } from '@material-ui/core';
import { cloneDeep, isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { IPublisher } from '~/src/dash/api/console-api';
import { ISmallBidder } from '~/src/dash/api/filters/bidders';
import {
  createPlatformConnectionOfferHistory,
  fetchPlatformConnectionOfferHistory,
} from '~/src/dash/apiV2/platform-connect/platform-connect';
import { FocusedTextfield } from '~/src/dash/components/focused-textfield';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { InfoTooltipIcon } from '~/src/dash/components/icons/InfoIcon';
import { IconText } from '~/src/dash/components/IconText';
import { IListViewTableDataCell } from '~/src/dash/components/tables/ListViewTable/common';
import { TruncatedList } from '~/src/dash/components/TruncatedList';
import {
  borderRadius,
  ButtonV2,
  Col,
  Cols,
  Div,
  Row,
  Rows,
  spacing,
  Span,
  Text,
  TextButton,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  getRoutePath,
  RouteBidderSettingsExisting,
  RouteChannelExisting,
} from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  EPlatformConnectionActionType,
  EPlatformConnectStatus,
  IPlatformConnectionDealExtRow,
  IPlatformConnectionOfferHistory,
  IPlatformConnectUiParams,
  RowLineBreak,
} from '~/src/dash/views/platform-connect/common';

interface ICartDrawerProps {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  publisher: IPublisher;
  activeRowInfo: Record<string, IListViewTableDataCell>;
  dealID?: number;
  impressionsGoal?: number;
  dealExtensionOpportunitiesRows?: IPlatformConnectionDealExtRow[];
  setDealExtensionOpportunitiesRows?: React.Dispatch<
    React.SetStateAction<IPlatformConnectionDealExtRow[]>
  >;
  onFetchUniqueDemandOpportunitiesList?: (params: IPlatformConnectUiParams) => void;
  handleToggleActive: (
    row: Record<string, IListViewTableDataCell>,
    status: EPlatformConnectStatus,
  ) => void;
  setError: React.Dispatch<React.SetStateAction<any>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  dealName: string;
  deliveryBidders: ISmallBidder[];
  handleRenderStatus: (row: Record<string, IListViewTableDataCell>) => JSX.Element;
  handleGetCurrencyValue: (row: Record<string, IListViewTableDataCell>) => string;
}

export const ConnectionsDrawer = ({
  isModalOpen,
  setIsModalOpen,
  publisher,
  activeRowInfo,
  handleToggleActive,
  setError,
  setSnackbarMessage,
  dealName,
  deliveryBidders,
  handleRenderStatus,
  handleGetCurrencyValue,
}: ICartDrawerProps) => {
  const { session, user, currencySymbol, currencyType, isSuperUser } = useBaseStore();
  const [currActiveRowInfo, setCurrActiveRowInfo] = useState(activeRowInfo);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isChangeConnectionStatusConfirmation, setIsChangeConnectionStatusConfirmation] =
    useState(false);

  // notes
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [currNote, setCurrNote] = useState('');
  const [offerHistory, setOfferHistory] = useState([]);

  useEffect(() => {
    unstable_batchedUpdates(() => {
      setIsButtonDisabled(
        !IS_AUTHORIZED(['write_manage_seller_requests'], user.user).isAuthorized ||
          !(
            activeRowInfo?.ActionPublisherID?.value === publisher.ID ||
            parseInt(activeRowInfo?.ActionPublisherID?.value, 10) <= 0
          ),
      );
      setCurrActiveRowInfo(activeRowInfo);
    });
  }, [activeRowInfo]);

  useEffect(() => {
    setOfferHistory((currActiveRowInfo?.PlatformConnectionOfferHistory?.value || []).reverse());
  }, [currActiveRowInfo]);

  const onCloseDrawer = () => {
    unstable_batchedUpdates(() => {
      setIsModalOpen(false);
      setIsAddingNote(false);
      setCurrNote('');
      setIsChangeConnectionStatusConfirmation(false);
    });
  };

  const handleSendNote = () => {
    track('PC - Notes', { is_super_user: isSuperUser }, user.user);
    const data = {
      PublisherID: parseInt(publisher.ID, 10),
      PlatformConnectionID: parseInt(activeRowInfo?.ID?.value as string, 10),
      ProposedBidFloor: activeRowInfo?.ProposedCPM?.value,
      ActionType: EPlatformConnectionActionType.PLATFORM_CONNECTION_ACTION_TYPE_NOTE,
      UserID: parseInt(user.user.ID, 10),
      Note: currNote,
    } as Partial<IPlatformConnectionOfferHistory>;
    createPlatformConnectionOfferHistory(
      publisher.ID,
      data,
      activeRowInfo?.BuyerPublisherID?.value,
      activeRowInfo?.SellerPublisherID?.value,
    )
      .then(() => {
        fetchPlatformConnectionOfferHistory(
          publisher.ID,
          activeRowInfo?.ID?.value,
          currencySymbol,
          currencyType,
        )
          .then((history) => {
            const newActiveRowInfo = cloneDeep(currActiveRowInfo);
            if (newActiveRowInfo.PlatformConnectionOfferHistory) {
              newActiveRowInfo.PlatformConnectionOfferHistory.value = history;
              setCurrActiveRowInfo(newActiveRowInfo);
            }
            unstable_batchedUpdates(() => {
              setIsAddingNote(false);
              setCurrNote('');
            });
          })
          .catch((err) => {
            unstable_batchedUpdates(() => {
              setError(err);
              setSnackbarMessage('Error retrieving data.');
            });
          });
      })
      .catch((err) => {
        unstable_batchedUpdates(() => {
          setError(err);
          setSnackbarMessage('Error sending note.');
        });
      });
  };

  const handleToggleActiveAndCloseDrawer = (status: EPlatformConnectStatus) => {
    track('PC - Sets inactive/active', { is_super_user: isSuperUser }, user.user);
    handleToggleActive(activeRowInfo, status);
    onCloseDrawer();
  };

  const getBidderSettingsRoute = (id: string): string => {
    track('PC - Links', { is_super_user: isSuperUser }, user.user);
    return getRoutePath(RouteBidderSettingsExisting, extractRouteParams({ session, user }), {
      b: id,
    });
  };

  const getChannelRoute = (id: string): string => {
    track('PC - Links', { is_super_user: isSuperUser }, user.user);
    return getRoutePath(RouteChannelExisting, extractRouteParams({ session, user }), {
      ch: id,
    });
  };

  return (
    <>
      <Drawer anchor='right' open={isModalOpen} onClose={onCloseDrawer}>
        <Rows
          paddingBottom={spacing.medium}
          gap={spacing.medium}
          style={{ width: '40vw', overflow: 'auto', maxHeight: '100vh' }}
        >
          <Row noPadding>
            <Rows>
              <Row paddingBottom={spacing.none}>
                <Cols
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Col>{handleRenderStatus(activeRowInfo)}</Col>
                  <Col>
                    <TextButton
                      color={publicaColors.darkGray}
                      style={{
                        display: 'flex',
                        borderRadius: borderRadius.small,
                        minWidth: '80px',
                        minHeight: '32px',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                      }}
                      aria-label='Close'
                      onClick={onCloseDrawer}
                    >
                      <IconText
                        bold
                        color={publicaColors.darkGray}
                        icon='close'
                        text='Close'
                        extraSmall
                        gap={spacing.extraSmall}
                      />
                    </TextButton>
                  </Col>
                </Cols>
              </Row>
              <Row paddingBottom={spacing.none} paddingLeft={spacing.large}>
                <Text large heavy color={publicaColors.darkGray}>
                  {!isEmpty(activeRowInfo) ? activeRowInfo.Name?.value : 'Anonymous Seller'}
                </Text>
              </Row>
            </Rows>
          </Row>
          <RowLineBreak />
          <Row noVerticalPadding>
            <Rows gap={spacing.small}>
              <Row noPadding>
                <IconText bold icon='trend-up' text='Delivery' small />
              </Row>
              {publisher.ID === activeRowInfo?.SellerPublisherID?.value &&
                activeRowInfo?.SellerBidderID?.value && (
                  <Row largeHorizontalPadding noVerticalPadding>
                    <Rows>
                      <Row noPadding>
                        <IconText
                          bold
                          color={publicaColors.darkGray}
                          icon='bidders'
                          text='Bidder'
                          small
                        />
                      </Row>
                      <Row noPadding>
                        <Cols
                          paddingLeft={spacing.small}
                          gap={spacing.extraSmall}
                          style={{ alignItems: 'center' }}
                        >
                          <Col>
                            <Link
                              target='_blank'
                              style={{
                                textDecoration: 'none',
                                color: publicaColors.blue,
                                display: 'inline-block',
                                width: '100%',
                              }}
                              href={
                                '#' + getBidderSettingsRoute(activeRowInfo?.SellerBidderID?.value)
                              }
                            >
                              {`${
                                activeRowInfo?.Type?.value === 'Legacy'
                                  ? 'Manually Created Bidder'
                                  : 'Auto-created Bidder'
                              } (${activeRowInfo?.SellerBidderID?.value})`}
                            </Link>
                          </Col>
                          <Col displayFlex>
                            <IconV2 color={publicaColors.blue} name='externalLink' />
                          </Col>
                        </Cols>
                      </Row>
                    </Rows>
                  </Row>
                )}
              {publisher.ID === activeRowInfo?.BuyerPublisherID?.value &&
                !isEmpty(deliveryBidders) && (
                  <Row largeHorizontalPadding noVerticalPadding>
                    <Rows>
                      <Row noPadding>
                        <IconText
                          bold
                          color={publicaColors.darkGray}
                          icon='bidders'
                          text='Bidder'
                          small
                        />
                      </Row>
                      <Row noPadding>
                        <Rows gap={spacing.small}>
                          {(deliveryBidders || []).map((bidder) => {
                            return (
                              <Row noPadding>
                                <Cols
                                  paddingLeft={spacing.small}
                                  gap={spacing.extraSmall}
                                  style={{ alignItems: 'center' }}
                                >
                                  <Col>
                                    <Link
                                      target='_blank'
                                      style={{
                                        textDecoration: 'none',
                                        color: publicaColors.blue,
                                        display: 'inline-block',
                                        width: '100%',
                                      }}
                                      href={'#' + getBidderSettingsRoute(bidder.ID)}
                                    >
                                      {`${bidder.Name} (${bidder.ID})`}
                                    </Link>
                                  </Col>
                                  <Col displayFlex>
                                    <IconV2 color={publicaColors.blue} name='externalLink' />
                                  </Col>
                                </Cols>
                              </Row>
                            );
                          })}
                        </Rows>
                      </Row>
                    </Rows>
                  </Row>
                )}
              {publisher.ID === activeRowInfo?.BuyerPublisherID?.value &&
                activeRowInfo?.BuyerChannelID?.value && (
                  <Row largeHorizontalPadding noVerticalPadding>
                    <Rows>
                      <Row noPadding>
                        <IconText
                          bold
                          color={publicaColors.darkGray}
                          icon='channels'
                          text='Channel'
                          small
                        />
                      </Row>
                      <Row noPadding>
                        <Cols
                          paddingLeft={spacing.small}
                          gap={spacing.extraSmall}
                          style={{ alignItems: 'center' }}
                        >
                          <Col>
                            <Link
                              target='_blank'
                              style={{
                                textDecoration: 'none',
                                color: publicaColors.blue,
                                display: 'inline-block',
                                width: '100%',
                              }}
                              href={'#' + getChannelRoute(activeRowInfo?.BuyerChannelID?.value)}
                            >
                              {`${
                                activeRowInfo?.Type?.value === 'Legacy'
                                  ? 'Manually Created Channel'
                                  : 'Auto-created Channel'
                              } (${activeRowInfo?.BuyerChannelID?.value})`}
                            </Link>
                          </Col>
                          <Col displayFlex>
                            <IconV2 color={publicaColors.blue} name='externalLink' />
                          </Col>
                        </Cols>
                      </Row>
                    </Rows>
                  </Row>
                )}
            </Rows>
          </Row>
          <RowLineBreak />
          <Row noVerticalPadding>
            <Rows gap={spacing.small}>
              {activeRowInfo?.Type?.value === 'Deal Extension' && (
                <Row noPadding>
                  <Rows>
                    <Row noPadding>
                      <IconText
                        small
                        bold
                        color={publicaColors.darkGray}
                        icon='handshake'
                        text='Deal Extension'
                      />
                    </Row>
                    {!isEmpty(dealName) && (
                      <Row noVerticalPadding paddingLeft={spacing.large}>
                        <Text
                          small
                          color={publicaColors.gray}
                          style={{
                            wordWrap: 'break-word',
                          }}
                        >
                          {dealName}
                        </Text>
                      </Row>
                    )}
                  </Rows>
                </Row>
              )}
              {activeRowInfo?.Type?.value === 'Unique Demand' &&
                activeRowInfo?.DemandSource?.value && (
                  <Row noPadding>
                    <Rows>
                      <Row noPadding>
                        <IconText
                          small
                          bold
                          color={publicaColors.darkGray}
                          icon='globe'
                          text='Demand Sources'
                        />
                      </Row>
                      <Row noVerticalPadding paddingLeft={spacing.large}>
                        <Text small color={publicaColors.gray}>
                          {activeRowInfo?.DemandSource?.value}
                        </Text>
                      </Row>
                    </Rows>
                  </Row>
                )}
              <Row noPadding>
                <Rows>
                  <Row noPadding>
                    <Cols
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Col>
                        <IconText
                          small
                          bold
                          color={publicaColors.darkGray}
                          icon='shopping'
                          iconColor={publicaColors.black}
                          text='CPM'
                        />
                      </Col>
                      {/* Existing Pending Offer */}
                      <Col>
                        <Text bold small color={publicaColors.darkGray}>
                          {handleGetCurrencyValue(activeRowInfo)}
                        </Text>
                      </Col>
                    </Cols>
                  </Row>
                </Rows>
              </Row>
              {!isEmpty(activeRowInfo?.AdsTxt?.value) && (
                <Row noPadding>
                  <Rows>
                    <Row noPadding>
                      <IconText
                        small
                        bold
                        color={publicaColors.darkGray}
                        icon='settings'
                        iconColor={publicaColors.black}
                        text='Options'
                      />
                    </Row>
                    <Row noPadding paddingLeft={spacing.large}>
                      <TruncatedList
                        displayStr={activeRowInfo?.AdsTxt?.value}
                        truncatedDescription={'Show Ads.txt'}
                        disableSplitOnComma
                      />
                    </Row>
                  </Rows>
                </Row>
              )}
            </Rows>
          </Row>
          <RowLineBreak />
          <Row noVerticalPadding>
            <Rows gap={spacing.small}>
              {!isEmpty(offerHistory) ? (
                <Row noPadding>
                  <Rows>
                    <Row noPadding paddingBottom={spacing.small}>
                      <Cols
                        style={{
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Col>
                          <Cols>
                            <Col>
                              <IconText
                                small
                                bold
                                color={publicaColors.darkGray}
                                icon='message'
                                iconColor={publicaColors.veryDarkGray}
                                text='Notes'
                              />
                            </Col>
                            <Col>
                              <InfoTooltipIcon
                                tooltipText='Sending only personal notes will not notify the Publisher. A notification is only sent with set inactive/active at this time.'
                                tooltipPlacement='top'
                              />
                            </Col>
                          </Cols>
                        </Col>
                        <Col>
                          <ButtonV2
                            style={{
                              borderRadius: '20px',
                            }}
                            clearBackground
                            hoverColor={publicaColors.lighterGray}
                            onClick={() => setIsAddingNote(true)}
                            disabled={
                              !IS_AUTHORIZED(['write_manage_seller_requests'], user.user)
                                .isAuthorized
                            }
                          >
                            <Text
                              extraSmall
                              style={{
                                color: publicaColors.blue,
                              }}
                            >
                              + Add personal note
                            </Text>
                          </ButtonV2>
                        </Col>
                      </Cols>
                    </Row>
                    {isAddingNote && (
                      <Row noPadding>
                        <Rows
                          style={{
                            overflow: 'auto',
                            maxHeight: '40vh',
                          }}
                        >
                          <Row
                            noVerticalPadding
                            paddingLeft={spacing.large}
                            paddingRight={spacing.medium}
                          >
                            <FocusedTextfield
                              fullWidth
                              multiline
                              minRows={3}
                              label=''
                              name='Notes'
                              value={currNote}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setCurrNote(e.target.value)
                              }
                              disabled={
                                !IS_AUTHORIZED(['write_manage_seller_requests'], user.user)
                                  .isAuthorized
                              }
                            />
                          </Row>
                          <Row>
                            <Cols
                              gap={spacing.small}
                              style={{
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Col>
                                <ButtonV2
                                  style={{
                                    borderRadius: '20px',
                                    minWidth: '80px',
                                    minHeight: '32px',
                                  }}
                                  aria-label='Send'
                                  onClick={handleSendNote}
                                >
                                  <IconText
                                    bold
                                    extraSmall
                                    color={publicaColors.white}
                                    icon='send'
                                    text='Send'
                                    noPadding
                                  />
                                </ButtonV2>
                              </Col>
                              <Col>
                                <ButtonV2
                                  style={{
                                    borderRadius: '20px',
                                    padding: `${spacing.extraSmall} ${spacing.small}`,
                                    minWidth: '80px',
                                    minHeight: '32px',
                                  }}
                                  backgroundColor={publicaColors.lighterGray}
                                  hoverColor={publicaColors.lightGray}
                                  onClick={() => {
                                    setIsAddingNote(false);
                                    setCurrNote('');
                                  }}
                                >
                                  <Text
                                    bold
                                    extraSmall
                                    style={{
                                      fontFamily: 'Roboto',
                                      color: publicaColors.darkGray,
                                    }}
                                  >
                                    Cancel
                                  </Text>
                                </ButtonV2>
                              </Col>
                            </Cols>
                          </Row>
                        </Rows>
                      </Row>
                    )}
                  </Rows>
                </Row>
              ) : (
                <Row noPadding>
                  <Cols>
                    <Col>
                      <IconText
                        small
                        bold
                        color={publicaColors.darkGray}
                        icon='message'
                        iconColor={publicaColors.veryDarkGray}
                        text='Notes'
                      />
                    </Col>
                    <Col>
                      <InfoTooltipIcon
                        tooltipText='Sending only personal notes will not notify the Publisher. A notification is only sent with set inactive/active at this time.'
                        tooltipPlacement='top'
                      />
                    </Col>
                  </Cols>
                </Row>
              )}
              {!isEmpty(offerHistory) && (
                <Row noPadding>
                  <Rows
                    gap={spacing.large}
                    style={{
                      overflow: 'auto',
                      maxHeight: '30vh',
                    }}
                    extraLargeHorizontalPadding
                    paddingBottom={isEmpty(offerHistory) ? spacing.none : spacing.medium}
                  >
                    {offerHistory.map((history: IPlatformConnectionOfferHistory) => {
                      return (
                        <Row noPadding>
                          <Rows gap={spacing.small}>
                            <Row noPadding>
                              <Cols>
                                <Col paddingRight={spacing.extraSmall}>
                                  {history.ActionType ===
                                  EPlatformConnectionActionType.PLATFORM_CONNECTION_ACTION_TYPE_NOTE ? (
                                    <Text extraSmall>
                                      {`${history.User?.firstname} ${history.User?.lastname} -`}
                                    </Text>
                                  ) : (
                                    <Text extraSmall>Automatic Note -</Text>
                                  )}
                                </Col>
                                <Col>
                                  <Text extraSmall>{`[${moment(history.CreatedAt)
                                    .tz(publisher.Timezone)
                                    .format('MMM DD, YYYY - hh:mm A')}]`}</Text>
                                </Col>
                              </Cols>
                            </Row>
                            <Row noPadding>
                              <Text
                                small
                                style={{
                                  backgroundColor: publicaColors.veryLightBlue,
                                  borderRadius: borderRadius.base,
                                  padding: '8px 16px',
                                }}
                              >
                                {history.Note}
                              </Text>
                            </Row>
                          </Rows>
                        </Row>
                      );
                    })}
                  </Rows>
                </Row>
              )}
            </Rows>
          </Row>
          <RowLineBreak />
          {/* Set Inactive Button */}
          {activeRowInfo?.CustomLeftColumn?.value ===
            EPlatformConnectStatus.PLATFORM_CONNECTION_LIVE && (
            <Row noVerticalPadding>
              {!isChangeConnectionStatusConfirmation ? (
                <Rows
                  gap={spacing.medium}
                  style={{
                    alignItems: 'center',
                  }}
                >
                  <Row noPadding>
                    <ButtonV2
                      clearBackground
                      style={{
                        border: `1px solid ${
                          isButtonDisabled ? publicaColors.lighterGray : publicaColors.mahogany
                        }`,
                        borderRadius: borderRadius.small,
                        padding: spacing.small,
                      }}
                      disabled={isButtonDisabled}
                      aria-label='Set Inactive'
                      onClick={() => setIsChangeConnectionStatusConfirmation(true)}
                    >
                      <Text
                        bold
                        extraSmall
                        color={isButtonDisabled ? publicaColors.mediumGray : publicaColors.mahogany}
                      >
                        Set inactive
                      </Text>
                    </ButtonV2>
                  </Row>
                  <Row noPadding>
                    <Div displayFlex>
                      <Text extraSmall color={publicaColors.darkGray}>
                        {activeRowInfo?.SellerPublisherID?.value === publisher.ID &&
                          `Demand from this connection will stop and associated bidder set to inactive.`}
                        {activeRowInfo?.BuyerPublisherID?.value === publisher.ID &&
                          `Demand from this connection will stop and associated channel set to inactive.`}
                      </Text>
                    </Div>
                  </Row>
                </Rows>
              ) : (
                <Rows
                  gap={spacing.medium}
                  style={{
                    alignItems: 'center',
                  }}
                >
                  <Row noPadding>
                    <Cols
                      style={{
                        justifyContent: 'center',
                      }}
                      gap={spacing.medium}
                    >
                      <Col>
                        <ButtonV2
                          clearBackground
                          style={{
                            backgroundColor: publicaColors.mahogany,
                            borderRadius: borderRadius.small,
                            padding: spacing.small,
                          }}
                          disabled={isButtonDisabled}
                          aria-label='Confirm Set Inactive'
                          onClick={() => {
                            isButtonDisabled
                              ? null
                              : handleToggleActiveAndCloseDrawer(
                                  EPlatformConnectStatus.PLATFORM_CONNECTION_INACTIVE,
                                );
                          }}
                        >
                          <Text bold extraSmall color={publicaColors.white}>
                            Confirm set inactive
                          </Text>
                        </ButtonV2>
                      </Col>
                      <Col>
                        <ButtonV2
                          hoverColor='#ECECEC'
                          backgroundColor={publicaColors.lighterGray}
                          style={{
                            borderRadius: borderRadius.small,
                            padding: spacing.small,
                          }}
                          aria-label='Cancel'
                          onClick={() => {
                            setIsChangeConnectionStatusConfirmation(false);
                          }}
                        >
                          <IconText
                            bold
                            extraSmall
                            gap={spacing.extraSmall}
                            icon='close'
                            text='Cancel'
                            noPadding
                          />
                        </ButtonV2>
                      </Col>
                    </Cols>
                  </Row>
                  <Row noPadding>
                    <Div displayFlex>
                      <Text extraSmall color={publicaColors.darkGray}>
                        <Span bold>Check check </Span>- are you sure you want to deactivate?
                      </Text>
                    </Div>
                  </Row>
                </Rows>
              )}
            </Row>
          )}
          {/* Set Active Button */}
          {activeRowInfo?.CustomLeftColumn?.value ===
            EPlatformConnectStatus.PLATFORM_CONNECTION_INACTIVE && (
            <Row noVerticalPadding>
              {!isChangeConnectionStatusConfirmation ? (
                <Rows
                  gap={spacing.medium}
                  style={{
                    alignItems: 'center',
                  }}
                >
                  <Row noPadding>
                    <ButtonV2
                      clearBackground
                      style={{
                        border: `1px solid ${
                          isButtonDisabled ? publicaColors.lighterGray : publicaColors.green
                        }`,
                        borderRadius: borderRadius.small,
                        padding: spacing.small,
                      }}
                      disabled={isButtonDisabled}
                      aria-label='Set Active'
                      onClick={() => setIsChangeConnectionStatusConfirmation(true)}
                    >
                      <Text
                        bold
                        extraSmall
                        color={isButtonDisabled ? publicaColors.mediumGray : publicaColors.green}
                      >
                        Set active
                      </Text>
                    </ButtonV2>
                  </Row>
                  <Row noPadding>
                    <Div displayFlex>
                      <Text extraSmall color={publicaColors.darkGray}>
                        Only the buyer or seller who initially set the connection to inactive can
                        reactivate it.
                      </Text>
                    </Div>
                  </Row>
                </Rows>
              ) : (
                <Rows
                  gap={spacing.medium}
                  style={{
                    alignItems: 'center',
                  }}
                >
                  <Row noPadding>
                    <Cols
                      style={{
                        justifyContent: 'center',
                      }}
                      gap={spacing.medium}
                    >
                      <Col>
                        <ButtonV2
                          style={{
                            borderRadius: borderRadius.small,
                            padding: spacing.small,
                          }}
                          disabled={isButtonDisabled}
                          aria-label='Confirm Set Active'
                          onClick={() => {
                            isButtonDisabled
                              ? null
                              : handleToggleActiveAndCloseDrawer(
                                  EPlatformConnectStatus.PLATFORM_CONNECTION_LIVE,
                                );
                          }}
                        >
                          <Text bold extraSmall color={publicaColors.white}>
                            Confirm set active
                          </Text>
                        </ButtonV2>
                      </Col>
                      <Col>
                        <ButtonV2
                          hoverColor='#ECECEC'
                          backgroundColor={publicaColors.lighterGray}
                          style={{
                            borderRadius: borderRadius.small,
                            padding: spacing.small,
                          }}
                          aria-label='Cancel'
                          onClick={() => {
                            setIsChangeConnectionStatusConfirmation(false);
                          }}
                        >
                          <IconText
                            bold
                            extraSmall
                            gap={spacing.extraSmall}
                            icon='close'
                            text='Cancel'
                            noPadding
                          />
                        </ButtonV2>
                      </Col>
                    </Cols>
                  </Row>
                  <Row noPadding>
                    <Div displayFlex>
                      <Text extraSmall color={publicaColors.darkGray}>
                        <Span bold>Check check </Span>- ready to reactivate connection?
                      </Text>
                    </Div>
                  </Row>
                </Rows>
              )}
            </Row>
          )}
        </Rows>
      </Drawer>
    </>
  );
};
